"use client";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGraduationCap } from "@fortawesome/pro-solid-svg-icons/faGraduationCap";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import NextLink from "next/link";
import { styled } from "@mui/material/styles";
import { paletteOptions } from "@portal/app/_components/theme/options/palette";

import TruckLandscapePhoto from "@portal/app/_components/resources/images/truck_on_road_1500w.jpg";
import { Layout } from "@portal/app/_components/ui-elements/Layout";
import { JobBlurbCompanyDescription } from "@portal/app/company/_components/JobBlurb";

export type Job = {
  id: number;
  url: string;
  title: string;
  employmentType?: string;
  location: string | null;
  description?: string;
  departmentId: number | null;
  department: string | null;
  officeId: number | null;
  office: string | null;
  contentArray: string[];
};

const CompanyJobsPage = ({ jobs }: { jobs: Job[] }) => {
  const opsJobs = jobs.filter((job) => job.departmentId === 4010157003);
  const engJobs = jobs.filter((job) => job.departmentId === 4010159003);

  let attributionId: string | null;
  if (typeof window !== "undefined") {
    const urlParams = new URLSearchParams(window.location.search);
    attributionId = urlParams.get("gh_src");
  }

  const JobItem = ({ job, isDescriptionHidden }: { job: Job; isDescriptionHidden?: boolean }) => (
    <Paper>
      <NextLink
        href={`/company/jobs/${job.id}${attributionId ? "?gh_src=" + attributionId : ""}`}
        legacyBehavior
        passHref
      >
        <JobItemLink>
          <Typography variant="new_h3" sx={{ m: 0, mb: 1 }}>
            {job.title} {job.employmentType === "Part-time" && <span>Part-time</span>}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Chip label={job.location} size="small" color="primary" />
            <Chip label={job.employmentType} size="small" variant="outlined" />
          </Stack>
          {!isDescriptionHidden && <Typography paragraph>{job.description}</Typography>}
        </JobItemLink>
      </NextLink>
    </Paper>
  );

  return (
    <Layout footer="BOTH">
      <HeroWrapper>
        <img
          src={TruckLandscapePhoto.src}
          alt="Photo of a truck on open road, symbolizing progress"
          style={{
            backgroundColor: "#b1e6fa",
            objectFit: "contain",
            objectPosition: "center bottom",
            position: "absolute",
            height: "100%",
            width: "100%",
            inset: 0,
            color: "transparent",
          }}
        />
        <InnerContainer>
          <Typography variant="page_title" sx={{ fontSize: { lg: 40 }, textTransform: "uppercase" }}>
            Help people find their purpose
          </Typography>
          <Typography paragraph>
            Most people avoid career changes because it feels impossible. At Dreambound, we&apos;re
            making it possible again, building the products, infrastructure, and services that help
            millions of Americans start new futures.
          </Typography>
          <Typography paragraph>
            We&apos;re a small, passionate team on a big mission. If that sounds like your kind of
            challenge, we&apos;d love to hear from you.
          </Typography>
        </InnerContainer>
      </HeroWrapper>
      <ContentWrapper>
        <InnerContainer className="inner-container">
          <JobBlurbCompanyDescription />
          <StudentCTA>
            <FontAwesomeIcon icon={faGraduationCap} className="icon" />
            <div>
              <Typography className="heading">Looking for training or career programs?</Typography>
              <Typography className="body">
                This page is about joining our team. If you&apos;re looking for classes and schools,
                we can help with that too.
              </Typography>
            </div>
            <NextLink href="/">
              <Button variant="contained" size="small">
                Browse programs
              </Button>
            </NextLink>
          </StudentCTA>
          <Typography variant="page_title">Open roles</Typography>
          {jobs.length === 0 ? (
            <Typography paragraph>
              We don&apos;t have any open positions right now, but we&apos;re always interested in
              meeting talented people. Check back soon &mdash; we&apos;re growing fast.
            </Typography>
          ) : (
            <>
              <Typography variant="new_h2">Engineering, product, &amp; design</Typography>
              {engJobs.length ? (
                <div className="jobList">
                  {engJobs.map((job) => (
                    <JobItem key={job.id} job={job} />
                  ))}
                </div>
              ) : (
                <Typography paragraph>No engineering roles open at this time.</Typography>
              )}

              <Typography variant="new_h2">Operations, sales, &amp; growth</Typography>
              {opsJobs.length ? (
                <div className="jobList">
                  {opsJobs.map((job) => (
                    <JobItem key={job.id} job={job} />
                  ))}
                </div>
              ) : (
                <Typography paragraph>No operations roles open at this time.</Typography>
              )}
            </>
          )}
        </InnerContainer>
      </ContentWrapper>
    </Layout>
  );
};

const PAGE_CONTENT_MAX_WIDTH = 800;

const InnerContainer = styled(Box)`
  max-width: ${PAGE_CONTENT_MAX_WIDTH}px;
  margin: 0 auto;
  padding: 0 var(--horizontal-padding);
`;

const HeroWrapper = styled(Box)`
  padding: var(--horizontal-padding) 0 22vw 0;
  margin: var(--horizontal-padding) auto;
  width: 100%;
  min-height: 30vh;

  position: relative;

  ${({ theme }) => theme.breakpoints.up("md")} {
    max-width: unset;
    width: calc(100vw - var(--horizontal-padding));
    padding-top: max(var(--horizontal-padding), 5vw);
  }

  ${({ theme }) => theme.breakpoints.up(1400)} {
    max-width: 1400px;
    padding-top: 4rem;
    padding-bottom: 350px;
  }

  .inner-container {
    position: relative;
  }
`;

const ContentWrapper = styled(Box)`
  margin: var(--horizontal-padding) 0;
`;

const StudentCTA = styled(Box)`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.5rem;
  margin: 2rem 0;
  border-radius: 0.75rem;
  background: ${paletteOptions.dream.grayLighter};
  border: 1px solid ${paletteOptions.dream.borderColor};

  .icon {
    font-size: 1.5rem;
    color: ${paletteOptions.primary.main};
    flex-shrink: 0;
  }

  .heading {
    font-weight: 700;
    font-size: 1rem;
    color: ${paletteOptions.text.primary};
  }

  .body {
    font-size: 0.875rem;
    color: ${paletteOptions.secondary.dark};
    margin-top: 0.25rem;
  }

  a {
    flex-shrink: 0;
  }

  ${({ theme }) => theme.breakpoints.down("sm")} {
    flex-direction: column;
    text-align: center;

    a {
      width: 100%;
    }
  }
`;

const JobItemLink = styled("a")`
  display: block;
  padding: 1rem;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.palette.dream.grayLighter};
  }
`;

export default CompanyJobsPage;
